import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOMeta from "../../components/base/SEOMeta";

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const writeForUsJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Write for QuizVibe – Creator Program | Submit Your Quiz Idea",
    "description": "Join 200+ creators publishing viral personality quizzes on QuizVibe. Submit your quiz idea, reach 5M+ monthly players, and earn revenue share. Apply now!",
    "url": `${SITE_URL}/write-for-us`,
    "inLanguage": "en",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
        { "@type": "ListItem", "position": 2, "name": "Write for Us", "item": `${SITE_URL}/write-for-us` }
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "QuizVibe",
      "url": SITE_URL
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do I need to be a professional writer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Not at all! We care more about creativity and cultural awareness than grammar perfection. If you know what teens love, you're qualified."
        }
      },
      {
        "@type": "Question",
        "name": "How long should a quiz be?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We publish quizzes with 6–10 questions, each with 4 answer options. Image-based answer options perform significantly better."
        }
      },
      {
        "@type": "Question",
        "name": "Can I submit multiple quizzes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Once your first quiz is approved, you can submit as many as you like. Active creators get priority review."
        }
      },
      {
        "@type": "Question",
        "name": "What topics work best?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Quizzes tied to trending games (Roblox, Toca Boca), popular anime, Disney, and personality/relationship themes consistently go viral."
        }
      },
      {
        "@type": "Question",
        "name": "When will I hear back?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We respond to every submission within 3–5 business days. If your idea is a fit, we'll reach out to kickstart the full writing process."
        }
      },
      {
        "@type": "Question",
        "name": "Is there any exclusivity agreement?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No exclusivity. You retain full ownership of your ideas and can publish similar content elsewhere."
        }
      }
    ]
  }
];

const perks = [
  {
    icon: "ri-global-line",
    title: "Massive Reach",
    color: "from-[#FF6B6B] to-[#FF8E53]",
    desc: "Your quiz could be seen by 5M+ players every month across 120+ countries.",
  },
  {
    icon: "ri-copyright-line",
    title: "Full Credit",
    color: "from-[#FBBF24] to-[#F59E0B]",
    desc: "Your name and bio appear on every quiz you create. Build your creator brand.",
  },
  {
    icon: "ri-share-2-line",
    title: "Viral Potential",
    color: "from-[#6EE7B7] to-[#3B82F6]",
    desc: "Our quizzes spread through TikTok, Pinterest & Facebook. Go viral overnight.",
  },
  {
    icon: "ri-money-dollar-circle-line",
    title: "Revenue Share",
    color: "from-[#A78BFA] to-[#EC4899]",
    desc: "Top creators earn ad revenue share based on quiz performance and engagement.",
  },
];

const steps = [
  { num: "01", title: "Submit Your Idea", desc: "Fill in the form below with your quiz concept, target audience, and 5 sample questions." },
  { num: "02", title: "We Review It", desc: "Our editorial team reviews every submission within 3–5 business days and gives detailed feedback." },
  { num: "03", title: "Write & Polish", desc: "Once approved, write all 10 questions with 4 image-based answer options and 4 unique result descriptions." },
  { num: "04", title: "Go Live!", desc: "We publish your quiz with your creator credit. Watch the plays and shares roll in!" },
];

const faqs = [
  { q: "Do I need to be a professional writer?", a: "Not at all! We care more about creativity and cultural awareness than grammar perfection. If you know what teens love, you're qualified." },
  { q: "How long should a quiz be?", a: "We publish quizzes with 6–10 questions, each with 4 answer options. Image-based answer options perform significantly better." },
  { q: "Can I submit multiple quizzes?", a: "Yes! Once your first quiz is approved, you can submit as many as you like. Active creators get priority review." },
  { q: "What topics work best?", a: "Quizzes tied to trending games (Roblox, Toca Boca), popular anime, Disney, and personality/relationship themes consistently go viral." },
  { q: "When will I hear back?", a: "We respond to every submission within 3–5 business days. If your idea is a fit, we'll reach out to kickstart the full writing process." },
  { q: "Is there any exclusivity agreement?", a: "No exclusivity. You retain full ownership of your ideas and can publish similar content elsewhere." },
];

export default function WriteForUsPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textareas = form.querySelectorAll("textarea");
    let hasOverLimit = false;
    textareas.forEach((ta) => {
      if ((ta as HTMLTextAreaElement).value.length > 500) hasOverLimit = true;
    });
    if (hasOverLimit) return;

    setSubmitting(true);
    try {
      const data = new FormData(form);
      const params = new URLSearchParams();
      data.forEach((val, key) => params.append(key, val.toString()));
      await fetch("https://readdy.ai/api/form/d71lu2k991ove783t2b0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setSubmitted(true);
      formRef.current?.reset();
      setCharCount(0);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOMeta
        title="Write for QuizVibe – Join Our Creator Program & Submit Quiz Ideas"
        description="Become a QuizVibe quiz creator! Submit personality quiz ideas, reach 5M+ monthly players across 120 countries, earn revenue share, and go viral on TikTok & Pinterest."
        keywords="write for QuizVibe, quiz creator program, submit quiz idea, personality quiz writer, earn from quizzes, viral quiz creator"
        canonicalPath="/write-for-us"
        ogImage="https://public.readdy.ai/ai/img_res/80d89a40da5d9b9501ac255a7dd99dea.jpg"
        jsonLd={writeForUsJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=creative%20writer%20content%20creator%20working%20laptop%20bright%20colorful%20workspace%20kawaii%20desk%20stationery%20pencil%20notebook%20vibrant%20pastel%20abstract%20background%20warm%20coral%20orange%20illustration&width=1440&height=650&seq=writeforus-hero&orientation=landscape"
            alt="Write for FunsTest"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B6B]/85 via-[#FF8E53]/78 to-[#FBBF24]/70" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-quill-pen-line text-white" />
            <span className="text-white text-sm font-semibold">Creator Program</span>
          </div>
          <h1
            className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Write Quizzes.<br />
            <span className="text-yellow-300">Go Viral.</span>
          </h1>
          <p className="text-white/90 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-8">
            Join 200+ creators publishing on FunsTest. Reach millions of teens, build your following, and earn revenue — all by doing what you love.
          </p>
          <a
            href="#submit-form"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#FF6B6B] font-extrabold rounded-full hover:scale-105 transition-transform cursor-pointer whitespace-nowrap"
          >
            Submit Your Idea ✍️
            <i className="ri-arrow-down-line" />
          </a>
        </div>
      </section>

      {/* Perks */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#FF6B6B] font-bold text-sm uppercase tracking-wider mb-2">Why Create With Us</p>
            <h2
              className="text-4xl font-extrabold text-gray-900"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              The Creator Advantage
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {perks.map((p) => (
              <div key={p.title} className="bg-[#FFF8F6] rounded-2xl p-6 border border-orange-50">
                <div className={`w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${p.color} mb-4`}>
                  <i className={`${p.icon} text-white text-xl`} />
                </div>
                <h3
                  className="font-extrabold text-gray-900 text-base mb-2"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {p.title}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#FFF8F6]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-[#FF6B6B] font-bold text-sm uppercase tracking-wider mb-2">Simple Process</p>
            <h2
              className="text-4xl font-extrabold text-gray-900"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              How It Works
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            {steps.map((s) => (
              <div key={s.num} className="bg-white rounded-2xl p-6 border border-gray-100 flex gap-5 items-start">
                <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] text-white font-extrabold text-sm"
                  style={{ fontFamily: "'Nunito', sans-serif" }}
                >
                  {s.num}
                </div>
                <div>
                  <h3 className="font-extrabold text-gray-900 text-sm mb-1.5" style={{ fontFamily: "'Nunito', sans-serif" }}>
                    {s.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Submit Form */}
      <section id="submit-form" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#FF6B6B] font-bold text-sm uppercase tracking-wider mb-2">Ready to Create?</p>
            <h2
              className="text-4xl font-extrabold text-gray-900 mb-3"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Submit Your Quiz Idea
            </h2>
            <p className="text-gray-500 text-sm">We review every submission. Expect a reply within 3–5 business days.</p>
          </div>

          {submitted ? (
            <div className="bg-gradient-to-br from-[#FF6B6B]/10 to-[#FBBF24]/10 border border-[#FF6B6B]/20 rounded-3xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] mb-6">
                <i className="ri-check-double-line text-white text-2xl" />
              </div>
              <h3
                className="text-2xl font-extrabold text-gray-900 mb-3"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                Submission Received! 🎉
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto mb-6">
                Our editorial team will review your idea and get back to you within 3–5 business days. Keep an eye on your inbox!
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="px-6 py-2.5 bg-[#FF6B6B] text-white text-sm font-bold rounded-full hover:bg-[#ff5252] transition-colors cursor-pointer whitespace-nowrap"
              >
                Submit Another Idea
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              data-readdy-form
              id="write-for-us-form"
              onSubmit={handleSubmit}
              className="bg-[#FFF8F6] rounded-3xl p-8 md:p-10 border border-orange-50 space-y-6"
            >
              {/* Name + Email */}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Full Name <span className="text-[#FF6B6B]">*</span></label>
                  <input
                    name="full_name"
                    type="text"
                    required
                    placeholder="Mia Hartwell"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#FF6B6B] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Email Address <span className="text-[#FF6B6B]">*</span></label>
                  <input
                    name="email"
                    type="email"
                    required
                    placeholder="you@email.com"
                    className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#FF6B6B] transition-colors"
                  />
                </div>
              </div>

              {/* Social Handle */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Social Media Handle (Optional)</label>
                <input
                  name="social_handle"
                  type="text"
                  placeholder="@yourhandle on TikTok, Instagram, etc."
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#FF6B6B] transition-colors"
                />
              </div>

              {/* Quiz Title */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Quiz Title <span className="text-[#FF6B6B]">*</span></label>
                <input
                  name="quiz_title"
                  type="text"
                  required
                  placeholder='e.g. "Which Roblox Game Matches Your Personality?"'
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#FF6B6B] transition-colors"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Category <span className="text-[#FF6B6B]">*</span></label>
                <select
                  name="category"
                  required
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#FF6B6B] transition-colors appearance-none cursor-pointer"
                >
                  <option value="">Select a category...</option>
                  <option value="Sandbox Game">Sandbox Game (Roblox, Toca Boca, Minecraft)</option>
                  <option value="Anime">Anime & Manga</option>
                  <option value="Movies">Movies & TV Shows</option>
                  <option value="Disney">Disney & Pixar</option>
                  <option value="Personality">Personality & Self-Discovery</option>
                  <option value="Love & Friendship">Love &amp; Friendship</option>
                  <option value="Action Game">Action & RPG Games</option>
                  <option value="Entertainment">General Entertainment</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Target Audience <span className="text-[#FF6B6B]">*</span></label>
                <select
                  name="target_audience"
                  required
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#FF6B6B] transition-colors appearance-none cursor-pointer"
                >
                  <option value="">Select target audience...</option>
                  <option value="Kids (under 12)">Kids (under 12)</option>
                  <option value="Tweens (10-13)">Tweens (10–13)</option>
                  <option value="Teens (13-17)">Teens (13–17)</option>
                  <option value="Young Adults (18-24)">Young Adults (18–24)</option>
                  <option value="All Ages">All Ages</option>
                </select>
              </div>

              {/* Quiz Description */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">
                  Quiz Concept Description <span className="text-[#FF6B6B]">*</span>
                </label>
                <textarea
                  name="quiz_description"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="Describe your quiz idea, what the player discovers, and why teens would love it..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#FF6B6B] transition-colors resize-none"
                />
                <p className={`text-xs mt-1 text-right ${charCount > 450 ? "text-[#FF6B6B]" : "text-gray-400"}`}>
                  {charCount}/500
                </p>
              </div>

              {/* Sample Questions */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">
                  3–5 Sample Questions <span className="text-[#FF6B6B]">*</span>
                </label>
                <textarea
                  name="sample_questions"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder={"1. Which color aesthetic describes your vibe?\n2. Choose your dream Saturday activity\n3. Pick a comfort food\n..."}
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#FF6B6B] transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 mt-1">These help our team evaluate the quiz&apos;s tone and direction.</p>
              </div>

              {/* Portfolio Link */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">Portfolio / Previous Work (Optional)</label>
                <input
                  name="portfolio_url"
                  type="url"
                  placeholder="https://yourwebsite.com or link to previous quizzes/articles"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#FF6B6B] transition-colors"
                />
              </div>

              {/* How Did You Hear */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">How did you find us?</label>
                <select
                  name="source"
                  className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-[#FF6B6B] transition-colors appearance-none cursor-pointer"
                >
                  <option value="">Select an option...</option>
                  <option value="TikTok">TikTok</option>
                  <option value="Pinterest">Pinterest</option>
                  <option value="Google Search">Google Search</option>
                  <option value="Friend Referral">Friend Referral</option>
                  <option value="Facebook">Facebook</option>
                  <option value="Instagram">Instagram</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white font-extrabold text-base rounded-2xl hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap disabled:opacity-60"
                style={{ fontFamily: "'Nunito', sans-serif" }}
              >
                {submitting ? "Submitting..." : "Submit My Quiz Idea ✍️"}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#FFF8F6]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[#FF6B6B] font-bold text-sm uppercase tracking-wider mb-2">Got Questions?</p>
            <h2
              className="text-4xl font-extrabold text-gray-900"
              style={{ fontFamily: "'Nunito', sans-serif" }}
            >
              Creator FAQ
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl border transition-all cursor-pointer ${openFaq === i ? "border-[#FF6B6B]/30" : "border-gray-100"}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="flex items-center justify-between px-6 py-4 gap-4">
                  <p className="text-sm font-bold text-gray-800">{faq.q}</p>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`text-[#FF6B6B] text-lg transition-transform ${openFaq === i ? "ri-subtract-line" : "ri-add-line"}`} />
                  </div>
                </div>
                {openFaq === i && (
                  <div className="px-6 pb-4">
                    <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2
            className="text-3xl font-extrabold text-white mb-3"
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Still not sure? Take a quiz first!
          </h2>
          <p className="text-white/80 text-sm mb-6">
            Experience the FunsTest magic from a player&apos;s perspective, then come back and make your own.
          </p>
          <Link
            to="/categories"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#FF6B6B] font-extrabold rounded-full hover:scale-105 transition-transform cursor-pointer whitespace-nowrap"
          >
            Browse Quizzes
            <i className="ri-gamepad-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
